import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import { BlockSkeleton } from "../interfaccia/loading";

const ACTIVE_STATUSES = new Set(["PENDING", "IN_PROGRESS", "APPROVED"]);
const TERMINAL_STATUSES = new Set(["SUCCESS", "ERROR", "REJECTED"]);

function statusLabel(status) {
  return {
    PENDING: "In preparazione", IN_PROGRESS: "In corso", READY_FOR_REVIEW: "Pronto per revisione",
    APPROVED: "Approvato", SUCCESS: "Completato", ERROR: "Errore", REJECTED: "Rifiutato",
    SKIPPED: "Saltato",
  }[status] || status || "Non creato";
}

function statusTone(status) {
  if (status === "SUCCESS") return "ready";
  if (status === "ERROR" || status === "REJECTED") return "error";
  if (["PENDING", "IN_PROGRESS", "APPROVED", "READY_FOR_REVIEW"].includes(status)) return "warning";
  return "muted";
}

function Badge({ status, children }) {
  return <span className={`alignment-badge alignment-badge--${statusTone(status)}`}>{children || statusLabel(status)}</span>;
}

function requestMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

function deliveryFrom(action, job) {
  const candidate = action?.candidateValue && typeof action.candidateValue === "object" ? action.candidateValue : {};
  return job?.metadata?.delivery || {
    targetSites: Array.isArray(candidate.targetSites) ? candidate.targetSites : [],
    copyToModa: candidate.copyToModa === true,
  };
}

function sourceFrom(action, job, sourceProductId) {
  const source = job?.metadata?.source?.source;
  return {
    site: source?.site || action?.source_site || action?.sourceSite || "--",
    productId: source?.productId || sourceProductId || "--",
  };
}

function normalizeLookupRecords(payload) {
  const values = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  return values.map((value) => ({
    code: String(value?.code ?? value?.brand_code ?? "").trim(),
    description: String(value?.description ?? value?.descrizione ?? value?.code ?? "").trim(),
  })).filter((value) => value.code);
}

function comparableBrand(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

function deliveryNeedsBrand(action, job) {
  const delivery = deliveryFrom(action, job);
  const targets = Array.isArray(delivery.targetSites) ? delivery.targetSites : [];
  return delivery.copyToModa === true || targets.some((site) => {
    const normalizedSite = String(site).toUpperCase();
    return normalizedSite === "VIG" || normalizedSite === "BOT";
  });
}

function sourceManufacturerFrom(snapshot, action, job) {
  const site = String(sourceFrom(action, job, null).site || "").toUpperCase();
  const value = snapshot?.sites?.[site]?.manufacturerName;
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function BrandRecoveryField({ selectId, options, value, manufacturerName, loading, disabled, onChange }) {
  return (
    <div className="alignment-image-brand mt3">
      <label htmlFor={selectId}><strong>Codice brand MODA richiesto</strong></label>
      <p className="f7 mt1 mb2">
        L’articolo MODA non ha un brand. Il codice scelto serve al percorso immagini e al trigger Prestashop;
        non modifica il brand anagrafico dell’articolo.
        {manufacturerName ? <span> Brand rilevato su Prestashop: <strong>{manufacturerName}</strong>.</span> : null}
      </p>
      <select
        id={selectId}
        aria-label="Codice brand MODA per il recupero immagini"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || loading}
      >
        <option value="">{loading ? "Caricamento brand..." : "Seleziona un brand"}</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>{option.description} ({option.code})</option>
        ))}
      </select>
    </div>
  );
}

function DeliverySummary({ action, job, sourceProductId }) {
  const source = sourceFrom(action, job, sourceProductId);
  const delivery = deliveryFrom(action, job);
  const targets = Array.isArray(delivery.targetSites) ? delivery.targetSites : [];
  return (
    <div className="alignment-image-route" aria-label="Percorso immagini">
      <div><span className="f7 scrittaColMedia">Sorgente</span><strong>{source.site}</strong><small>Prodotto {source.productId}</small></div>
      <span className="alignment-image-route-arrow" aria-hidden="true">→</span>
      <div><span className="f7 scrittaColMedia">Destinazioni</span><strong>{targets.length ? targets.join(" + ") : "Nessun Prestashop"}</strong><small>{delivery.copyToModa ? "Con copia cartella Moda" : "Senza copia Moda"}</small></div>
    </div>
  );
}

function BranchOutcomes({ sites }) {
  if (!Array.isArray(sites) || !sites.length) return null;
  return (
    <div className="alignment-image-outcomes" aria-label="Esiti consegna immagini">
      {sites.map((site) => (
        <div key={site.site} className="alignment-image-outcome">
          <div className="flex justify-between items-center" style={{ gap: "0.4rem" }}>
            <strong>{site.site === "MODA" ? "Cartella Moda" : `Prestashop ${site.site}`}</strong>
            <Badge status={site.status} />
          </div>
          <p className="f7 mt2 mb0">{site.message || "Nessun dettaglio disponibile."}</p>
        </div>
      ))}
    </div>
  );
}

function Confirmation({ operation, busy, onCancel, onConfirm }) {
  if (!operation) return null;
  const approve = operation === "approve";
  return (
    <div className="alignment-image-confirm mt3" role="dialog" aria-label={approve ? "Conferma approvazione immagini" : "Conferma rifiuto immagini"}>
      <strong>{approve ? "Avviare la consegna?" : "Rifiutare queste immagini?"}</strong>
      <p className="f7 mt1 mb2">{approve
        ? "Le immagini elaborate verranno inviate soltanto alle destinazioni indicate sopra."
        : "Il job verrà chiuso senza inviare immagini."}</p>
      <div className="flex flex-wrap" style={{ gap: "0.45rem" }}>
        <button type="button" className="alignment-button alignment-button--secondary" onClick={onCancel} disabled={busy}>Torna alla revisione</button>
        <button type="button" className="alignment-button alignment-button--primary" onClick={onConfirm} disabled={busy}>
          {busy ? "Operazione in corso" : approve ? "Conferma approvazione immagini" : "Conferma rifiuto immagini"}
        </button>
      </div>
    </div>
  );
}

export default function PrestashopAlignmentImageReview({
  itemId, sourceProductId, action, snapshot, token, canWrite, reviewAllowed, pageBusy, onRefresh, onReconcile,
}) {
  const [job, setJob] = useState(action?.imageJob || null);
  const [previews, setPreviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandLookupError, setBrandLookupError] = useState("");
  const [selectedBrandCode, setSelectedBrandCode] = useState("");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const basePath = `${apiUrl}/moda/prestashop-alignment/items/${itemId}/actions/${action.id}/image-review`;
  const needsBrand = deliveryNeedsBrand(action, job);
  const snapshotBrandValue = snapshot?.moda?.brandCode ?? snapshot?.moda?.brand;
  const snapshotBrand = typeof snapshotBrandValue === "string" ? snapshotBrandValue.trim() : "";
  const manufacturerName = sourceManufacturerFrom(snapshot, action, job);
  const resolvedBrandCode = (typeof job?.brandCode === "string" ? job.brandCode.trim() : "") || snapshotBrand || selectedBrandCode;
  const brandChoiceRequired = needsBrand && !job?.brandCode && !snapshotBrand;
  const brandReady = !needsBrand || Boolean(resolvedBrandCode);

  useEffect(() => {
    setSelectedBrandCode("");
    setBrandOptions([]);
    setBrandLookupError("");
  }, [itemId, action?.id]);

  useEffect(() => {
    if (!brandChoiceRequired) return undefined;
    let active = true;
    setBrandLoading(true);
    setBrandLookupError("");
    axios.get(apiUrl + "/moda/lookup/brand", { headers })
      .then((response) => {
        if (!active) return;
        const options = normalizeLookupRecords(response?.data);
        setBrandOptions(options);
        const manufacturer = comparableBrand(manufacturerName);
        const matches = manufacturer
          ? options.filter((option) => comparableBrand(option.code) === manufacturer || comparableBrand(option.description) === manufacturer)
          : [];
        const uniqueCodes = Array.from(new Set(matches.map((option) => option.code)));
        if (uniqueCodes.length === 1) setSelectedBrandCode(uniqueCodes[0]);
      })
      .catch((requestError) => {
        if (active) setBrandLookupError(requestMessage(requestError, "Impossibile caricare i brand MODA."));
      })
      .finally(() => { if (active) setBrandLoading(false); });
    return () => { active = false; };
  }, [brandChoiceRequired, headers, manufacturerName]);

  useEffect(() => { setJob(action?.imageJob || null); }, [action?.imageJob, action?.image_job_id]);

  const refreshJob = useCallback(async (options = {}) => {
    if (!action?.image_job_id && !job?.jobId) return null;
    try {
      const response = await axios.get(basePath, { headers });
      const next = response?.data?.data || null;
      if (next) setJob(next);
      if (next && TERMINAL_STATUSES.has(next.status) && options.reconcile !== false) {
        if (onReconcile) await onReconcile(true);
        if (onRefresh) await onRefresh();
      }
      return next;
    } catch (requestError) {
      if (!options.silent) setError(requestMessage(requestError, "Impossibile aggiornare il job immagini."));
      return null;
    }
  }, [action?.image_job_id, basePath, headers, job?.jobId, onReconcile, onRefresh]);

  useEffect(() => {
    if (!job?.jobId || !ACTIVE_STATUSES.has(job.status)) return undefined;
    const interval = window.setInterval(() => refreshJob({ silent: true }), 3000);
    return () => window.clearInterval(interval);
  }, [job?.jobId, job?.status, refreshJob]);

  useEffect(() => {
    let active = true;
    const objectUrls = [];
    const files = job?.status === "READY_FOR_REVIEW" && Array.isArray(job?.metadata?.files)
      ? job.metadata.files : [];
    if (!files.length) { setPreviews([]); return undefined; }
    Promise.all(files.map(async (file, index) => {
      try {
        const response = await axios.get(`${basePath}/preview/${index}`, { headers, responseType: "blob" });
        const url = typeof URL.createObjectURL === "function" ? URL.createObjectURL(response.data) : "";
        if (url) objectUrls.push(url);
        return { index, file, url, error: "" };
      } catch (requestError) {
        return { index, file, url: "", error: requestMessage(requestError, "Anteprima non disponibile.") };
      }
    })).then((values) => { if (active) setPreviews(values); });
    return () => {
      active = false;
      objectUrls.forEach((url) => { if (typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url); });
    };
  }, [basePath, headers, job?.metadata?.files, job?.status]);

  const createReview = async () => {
    if (!brandReady) {
      setError("Selezionare il codice brand MODA prima di preparare le immagini.");
      return;
    }
    setBusy(true); setError("");
    try {
      const response = await axios.post(basePath, brandPayload, { headers });
      setJob(response?.data?.data || null);
      if (onRefresh) await onRefresh();
    } catch (requestError) {
      setError(requestMessage(requestError, "Impossibile creare la revisione immagini."));
    } finally { setBusy(false); }
  };

  const runOperation = async () => {
    if (!confirmation) return;
    const operation = confirmation;
    setBusy(true); setError("");
    try {
      const response = await axios.post(`${basePath}/${operation}`, operation === "approve" ? brandPayload : {}, { headers });
      setJob(response?.data?.data || job);
      setConfirmation(null);
      if (onRefresh) await onRefresh();
    } catch (requestError) {
      setError(requestMessage(requestError, `Impossibile ${operation === "approve" ? "approvare" : "rifiutare"} le immagini.`));
      setConfirmation(null);
    } finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true); setError("");
    try {
      const response = await axios.post(`${basePath}/retry`, brandPayload, { headers });
      setJob(response?.data?.data || job);
      if (onRefresh) await onRefresh();
    } catch (requestError) {
      setError(requestMessage(requestError, "Retry immagini non avviato."));
    } finally { setBusy(false); }
  };

  const disabled = !canWrite || busy || pageBusy;
  const brandPayload = !job?.brandCode && resolvedBrandCode ? { brandCode: resolvedBrandCode } : {};
  return (
    <div className="alignment-image-review mt3">
      <DeliverySummary action={action} job={job} sourceProductId={sourceProductId} />
      {brandChoiceRequired ? (
        <BrandRecoveryField
          selectId={"alignment-image-brand-select-" + action.id}
          options={brandOptions}
          value={selectedBrandCode}
          manufacturerName={manufacturerName}
          loading={brandLoading}
          disabled={disabled}
          onChange={setSelectedBrandCode}
        />
      ) : needsBrand && resolvedBrandCode ? (
        <p className="f7 mt2 mb0">Codice brand usato per il recupero immagini: <strong>{resolvedBrandCode}</strong>.</p>
      ) : null}
      {!job ? (
        <div className="alignment-image-start mt3">
          <p className="f7 mt0">Scarica le immagini dal sito sorgente e prepara le anteprime. Nessun invio avviene prima della conferma.</p>
          <button type="button" className="alignment-button alignment-button--primary" onClick={createReview} disabled={disabled || !reviewAllowed || !brandReady}>
            {busy ? "Preparazione in corso" : "Prepara revisione immagini"}
          </button>
        </div>
      ) : (
        <div className="mt3">
          <div className="flex flex-wrap justify-between items-center" style={{ gap: "0.6rem" }}>
            <div><strong>Job immagini</strong><div className="f7 scrittaColMedia mt1">{job.lastStep || job.jobId}</div></div>
            <Badge status={job.status} />
          </div>
          {job.message ? <p className="f7 lh-copy mb0">{job.message}</p> : null}
          {ACTIVE_STATUSES.has(job.status) ? (
            <div className="alignment-image-progress mt3" aria-label="Elaborazione immagini in corso"><span /></div>
          ) : null}
          {job.status === "READY_FOR_REVIEW" ? (
            <div className="alignment-image-gallery mt3">
              {!previews.length && Array.isArray(job?.metadata?.files)
                ? job.metadata.files.map((file, index) => (
                  <BlockSkeleton key={file.processedFilename || index} tone="soft" height={280} />
                ))
                : null}
              {previews.map((preview) => (
                <figure key={preview.index} className="alignment-image-preview ma0">
                  <div className="alignment-image-preview-media">
                    {preview.url ? <img src={preview.url} alt={`Anteprima immagine ${preview.index + 1}`} /> : <div className="alignment-image-preview-error">{preview.error}</div>}
                  </div>
                  <figcaption>
                    <strong>Immagine {preview.index + 1}</strong>
                    <span>{preview.file.originalName || preview.file.processedFilename || "File elaborato"}</span>
                    <span>{preview.file.originalWidth || "--"} × {preview.file.originalHeight || "--"} px → {preview.file.processedWidth || "--"} × {preview.file.processedHeight || "--"} px</span>
                    {Array.isArray(preview.file.warnings) && preview.file.warnings.length ? (
                      <ul className="alignment-image-warnings mt2 mb0 pl3">{preview.file.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
                    ) : <span className="alignment-image-ok mt2">Nessun warning di ridimensionamento</span>}
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : null}
          <BranchOutcomes sites={job?.metadata?.sites} />
          <div className="flex flex-wrap mt3" style={{ gap: "0.45rem" }}>
            {job.status === "READY_FOR_REVIEW" ? <>
              <button type="button" className="alignment-button alignment-button--primary" onClick={() => setConfirmation("approve")} disabled={disabled || !brandReady}>Approva e avvia consegna</button>
              <button type="button" className="alignment-button alignment-button--secondary" onClick={() => setConfirmation("reject")} disabled={disabled}>Rifiuta immagini</button>
            </> : null}
            {job.status === "ERROR" ? <button type="button" className="alignment-button alignment-button--secondary" onClick={retry} disabled={disabled || !brandReady}>Riprova i rami falliti</button> : null}
            {job.status && job.status !== "READY_FOR_REVIEW" ? <button type="button" className="alignment-button alignment-button--secondary" onClick={() => refreshJob()} disabled={busy}>Aggiorna stato</button> : null}
          </div>
          <Confirmation operation={confirmation} busy={busy} onCancel={() => setConfirmation(null)} onConfirm={runOperation} />
        </div>
      )}
      {!canWrite ? <p className="f7 alignment-block-reason mb0">Permesso di scrittura richiesto per operare sul job.</p> : null}
      {brandLookupError ? <p role="alert" className="f7 alignment-block-reason mb0">{brandLookupError}</p> : null}
      {error ? <p role="alert" className="f7 alignment-block-reason mb0">{error}</p> : null}
    </div>
  );
}
